/**
 * The files exchanged in an offline standalone activation (EDGE-7188).
 *
 * An air-gapped appliance cannot reach the billing server to trade its activation key for the
 * identity that opens the software shipped in its image. Instead it writes an activation request
 * naming its serial and the recipient half of a key pair it generated; a user who can see the
 * appliance in Nimbra Console or Billing Manager uploads that file and gets back the identity
 * encrypted to that recipient, which only that appliance can open. The portal login is the
 * authorization, so the request carries no activation key. The appliance side lives in
 * appliance-ui's local controller; the billing side in billing-server's appliance license handlers.
 */

export const OFFLINE_ACTIVATION_REQUEST_TYPE = 'nimbra-standalone-activation-request'
export const OFFLINE_ACTIVATION_RESPONSE_TYPE = 'nimbra-standalone-activation-response'
export const OFFLINE_ACTIVATION_VERSION = 1

/** The JSON document the appliance writes and the billing server reads. */
export interface OfflineActivationRequestFile {
    type: typeof OFFLINE_ACTIVATION_REQUEST_TYPE
    version: typeof OFFLINE_ACTIVATION_VERSION
    serialNumber: string
    /** The age recipient (`age1...`) of the key pair the appliance keeps for offline activation. */
    recipient: string
}

/** The JSON document inside the age file the billing server returns. */
export interface OfflineActivationResponsePayload {
    type: typeof OFFLINE_ACTIVATION_RESPONSE_TYPE
    version: typeof OFFLINE_ACTIVATION_VERSION
    serialNumber: string
    /** The age identity line (`AGE-SECRET-KEY-1...`) that opens the software bundle. */
    decryptionKey: string
}

const AGE_RECIPIENT_PATTERN = /^age1[02-9ac-hj-np-z]{10,}$/

/** The message every malformed request gets, so the two billing routes and the appliance agree. */
export const INVALID_OFFLINE_ACTIVATION_REQUEST = 'The file is not a standalone activation request'

/**
 * Parse the text of an activation request file. Throws an Error with a user-facing message when
 * the text is not a request this version understands; the serial and recipient are trimmed.
 */
export function parseOfflineActivationRequest(text: string): OfflineActivationRequestFile {
    let parsed: unknown
    try {
        parsed = JSON.parse(text)
    } catch {
        throw new Error(INVALID_OFFLINE_ACTIVATION_REQUEST)
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(INVALID_OFFLINE_ACTIVATION_REQUEST)
    }
    const { type, version, serialNumber, recipient } = parsed as Record<string, unknown>
    if (type !== OFFLINE_ACTIVATION_REQUEST_TYPE) throw new Error(INVALID_OFFLINE_ACTIVATION_REQUEST)
    if (version !== OFFLINE_ACTIVATION_VERSION) {
        throw new Error('The activation request was made by a newer appliance release than this server supports')
    }
    if (typeof serialNumber !== 'string' || !serialNumber.trim()) throw new Error(INVALID_OFFLINE_ACTIVATION_REQUEST)
    if (typeof recipient !== 'string' || !AGE_RECIPIENT_PATTERN.test(recipient.trim())) {
        throw new Error(INVALID_OFFLINE_ACTIVATION_REQUEST)
    }
    return {
        type: OFFLINE_ACTIVATION_REQUEST_TYPE,
        version: OFFLINE_ACTIVATION_VERSION,
        serialNumber: serialNumber.trim(),
        recipient: recipient.trim(),
    }
}

const safeFilenamePart = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, '-')

export const offlineActivationRequestFileName = (serialNumber: string) =>
    `nimbra-activation-request-${safeFilenamePart(serialNumber)}.json`

export const offlineActivationResponseFileName = (serialNumber: string) =>
    `nimbra-activation-response-${safeFilenamePart(serialNumber)}.txt`
